import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Package, Truck, FileText, BellOff, Send, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { OrderDetailData } from "@/types/orders";
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_TYPE_LABELS,
  FULFILLMENT_TYPE_LABELS,
  OrderStatus,
  OrderType,
  FulfillmentType,
} from "@/config/order-constants";

interface OrderBasicInfoCardProps {
  order: OrderDetailData;
}

export function OrderBasicInfoCard({ order }: OrderBasicInfoCardProps) {
  const [isSending, setIsSending] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "d.M.yyyy", { locale: fi });
  };

  const handleSendManualNotification = async () => {
    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-status-notification", {
        body: {
          order_id: order.id,
          old_status: order.status,
          new_status: order.status,
          force_send: true,
        },
      });
      if (error) throw error;
      toast.success("Tilanpäivitysviesti lähetetty asiakkaalle");
    } catch (err) {
      toast.error("Lähetys epäonnistui", {
        description: err instanceof Error ? err.message : "Tuntematon virhe",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Tilauksen perustiedot
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Tilausnumero</p>
            <p className="font-medium">{order.order_number}</p>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Tila</p>
            <Badge className={cn(ORDER_STATUS_COLORS[order.status as OrderStatus])}>
              {ORDER_STATUS_LABELS[order.status as OrderStatus]}
            </Badge>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Tilauspäivä
            </p>
            <p className="font-medium">{formatDate(order.order_date)}</p>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Truck className="h-3.5 w-3.5" />
              Toimituspäivä
            </p>
            <p className="font-medium">{formatDate(order.delivery_date)}</p>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Asennuspäivä
            </p>
            <p className="font-medium">{formatDate(order.installation_date)}</p>
          </div>

          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Package className="h-3.5 w-3.5" />
              Tilauksen tyyppi
            </p>
            <p className="font-medium">
              {ORDER_TYPE_LABELS[order.order_type as OrderType]}
            </p>
          </div>

          <div className="space-y-1 col-span-2">
            <p className="text-sm text-muted-foreground">Toimitustapa</p>
            <p className="font-medium">
              {FULFILLMENT_TYPE_LABELS[order.fulfillment_type as FulfillmentType]}
            </p>
          </div>

          {order.suppress_notifications && (
            <div className="col-span-2 flex items-start justify-between gap-3 rounded-md border border-border bg-muted/60 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <BellOff className="h-4 w-4 shrink-0" />
                <span>Automaattiset asiakasviestit estetty</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs shrink-0"
                onClick={handleSendManualNotification}
                disabled={isSending || order.status === "draft" || order.status === "cancelled"}
              >
                {isSending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Send className="h-3 w-3" />
                )}
                Lähetä viesti nyt
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
